import { gsap } from "gsap"
import { ScrollTrigger } from "gsap/ScrollTrigger"
import React, { useEffect, useRef, useState } from "react"
import { useWindowSize } from "../lib/helpers"
import CoCreateContact from "./co-create-contact"
import ProjectsGrid from "./projects-grid"

import theme from "../styles/theme.module.scss"
import styles from "./philosophy-content.module.scss"

function PhilosophyContent(props) {
  if (typeof window !== `undefined`) {
    gsap.registerPlugin(ScrollTrigger);
  }
  const ref = useRef(null);
  const killed = useRef(false);
  const [height, setHeight] = useState();
  const { height: windowHeight } = useWindowSize();

  useEffect(() => {
    // check if animation has completed
    if (killed.current) return;

    // set responsive height for mobile
    setHeight(windowHeight * 0.01);

    const element = ref.current;
    const horizontalContainer = element.querySelector(".slides");
    const tl = gsap.timeline();

    // define gsap elements
    let sections = gsap.utils.toArray(".slide");
    let blocks = gsap.utils.toArray(".block");
    let frames = gsap.utils.toArray(".frame");
    let allProjects = document.querySelector(".project-grid");
    let footer = document.querySelector("footer");
    let bod = document.querySelector("body");
    let circles = document.querySelector(".circles");
    let slides = document.querySelectorAll(".slide");

    // only hide content on initial view before final frame
    if (!bod.classList.contains("hasAnimated")) {
      gsap.set(allProjects, { display: "none" });
      gsap.set(footer, { display: "none" });
    }

    // define gsap speed variables
    let shrinkSpeed = 1;
    let staggerSpeed = 0.06;

    // configure final state of content
    // scroll to top, kill triggers, and enable scrolling
    const releaseAnimation = () => {
      window.scrollTo(0, 0);
      bod.style = null;
      allProjects.style.display = "grid";
      gsap.set(allProjects, {
        display: "grid",
        marginTop: "100vh",
        position: "relative",
      });
      gsap.set(footer, { display: "grid", position: "relative" });
      bod.classList.add("hasAnimated");
      gsap.set(".philContent", { display: "none" });
      gsap.to(".finalFrameTitle, .finalFrameColumn", {
        opacity: 1,
        duration: 0.5,
        delay: 0.25,
      });
      let triggers = ScrollTrigger.getAll();
      triggers.forEach((trigger) => {
        trigger.kill();
      });
    };

    const hideMobileFrames = () => {
      gsap.set(".circleFrames", { display: "none" });
    };

    // DESKTOP
    // once all circles have animated
    // hide initial page content and
    // display final contact content
    const swapContent = () => {
      killed.current = true;
      gsap.set(".circles", { border: 0 });
      gsap.set(".finalFrameWrapper", { display: "grid" });
      bod.style.backgroundColor = "#f6e4d4";
      gsap.set(".philContent", { backgroundColor: "#f6e4d4" });
      gsap.set(".blocksInner", { opacity: 0 });
      window.location.href = "#create";
    };

    // MOBILE
    const swapContentMobile = () => {
      killed.current = true;
      gsap.set(".finalFrameWrapper", { display: "grid" });
      bod.style.backgroundColor = "#f6e4d4";
      gsap.set(".philContent", { backgroundColor: "#f6e4d4" });
      gsap.set(".blocksInner", { opacity: 0 });
      gsap.set("header", {
        background: "#f2eee9",
        borderBottom: "1px solid black",
      });
    };

    // DESKTOP
    // disable ability to scroll and
    // transition to final contact frame
    // by animating all circles successively
    // when user reaches end of section
    const animateOut = () => {
      if (!bod.classList.contains("hasAnimated")) {
        bod.style.overflow = "hidden";
        tl.to(".bgCircle", {
          opacity: 1,
          duration: 0.1,
          stagger: -staggerSpeed,
          onComplete: swapContent,
        })
          .set(".philContent", { backgroundColor: "#f6e4d4" })
          .to(".hbaBorder4, .hbaBg4", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorder3, .hbaBg3", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorder2, .hbaBg2", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorder5, .hbaBg5", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorder1, .hbaBg1", {
            opacity: 0,
            duration: staggerSpeed,
          })
          .to(".bgCircle", {
            opacity: 0,
            duration: 0.1,
            stagger: -staggerSpeed,
            onComplete: releaseAnimation,
          });
      }
    };

    // MOBILE
    const animateOutMobile = () => {
      if (!bod.classList.contains("hasAnimated")) {
        bod.style.overflow = "hidden";
        tl.set(".circlesFull", { opacity: 1 })
          .to(".bgCircleMobile", {
            opacity: 1,
            duration: 0.1,
            stagger: -staggerSpeed,
            onComplete: swapContentMobile,
          })
          .to(".hbaBorderm4, .hbaBgm4", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorderm3, .hbaBgm3", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorderm2, .hbaBgm2", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorderm5, .hbaBgm5", { opacity: 0, duration: staggerSpeed })
          .to(".hbaBorderm1, .hbaBgm1", {
            opacity: 0,
            duration: staggerSpeed,
          })
          .to(".bgCircleMobile", {
            opacity: 0,
            duration: 0.1,
            stagger: -staggerSpeed,
            onComplete: () => {
              releaseAnimation();
              hideMobileFrames();
            },
          });
      }
    };

    //define scrolltrigger functions based on screen width
    ScrollTrigger.matchMedia({
      // DESKTOP
      "(min-width: 1024px)": function () {
        // reload animation on window resize
        // to reconfigure scrollTrigger values
        let resizeTimer;

        const refreshAnim = () => {
          clearTimeout(resizeTimer);
          resizeTimer = setTimeout(function () {
            window.location.reload();
          }, 250);
        };

        window.addEventListener("resize", refreshAnim);
        // toggle nav header background color and border
        ScrollTrigger.create({
          trigger: ".hbaHeadline",
          start: "top top",
          onEnter: () => {
            gsap.set("header", {
              background: "#f2eee9",
              borderBottom: "1px solid black",
            });
          },
          onLeaveBack: () => {
            gsap.set("header", {
              background: "inherit",
              borderBottom: "",
            });
          },
        });

        // scroll initial slides horizontally
        gsap.to(sections, {
          xPercent: -100 * (sections.length - 1),
          ease: "none",
          scrollTrigger: {
            trigger: horizontalContainer,
            scrub: true,
            id: "horizontal",
            pin: true,
            start: "top top",
            onUpdate: ({ progress }) => {
              if (progress < 0.33) {
                gsap.to(".slides", { background: "#f2eee9", duration: 0.3 });
              } else if (progress > 0.33 && progress < 0.66) {
                gsap.to(".slides", { background: "#edecef", duration: 0.3 });
              } else {
                gsap.to(".slides", { background: "#f6e4d4", duration: 0.3 });
              }
            },
          },
        });

        // toggle position of circle content
        // essentially creating a manual sticky element
        ScrollTrigger.create({
          trigger: circles,
          start: "top top",
          onEnter: () => {
            gsap.set(".circles", { position: "fixed", overflow: "visible" });
          },
          onLeaveBack: () => {
            gsap.set(".circles", { position: "absolute", overflow: "hidden" });
          },
        });

        // set position fixed for final circle
        ScrollTrigger.create({
          trigger: ".hbaSlideFinal",
          start: "top bottom",
          onEnter: () => {
            gsap.set(".hbaBlock4", {
              position: "fixed",
              top: "6rem",
              width: "50vw",
              height: "calc(100% - 6rem)",
            });
          },
          onLeaveBack: () => {
            gsap.set(".hbaBlock4", { position: "static", top: "", width: "" });
          },
        });

        // toggle opacity of corresponding circle
        blocks.forEach((block, i) => {
          ScrollTrigger.create({
            trigger: block,
            start: "top center",
            onEnter: () => {
              gsap.to(".hbaBg" + (i + 1), { opacity: 1, duration: 0.5 });
              gsap.to(".hbaBorder" + (i + 1), { opacity: 1, duration: 0.5 });
            },
            onLeaveBack: () => {
              if (i != 0) {
                gsap.to(".hbaBg" + (i + 1), { opacity: 0, duration: 0.5 });
                gsap.to(".hbaBorder" + (i + 1), { opacity: 0, duration: 0.5 });
              }
            },
          });
        });

        // final animation of collapsing circles
        let tlFinal = gsap.timeline({
          scrollTrigger: {
            trigger: ".hbaSlideFinal",
            scrub: true,
            pin: true,
            id: "circles",
            start: "top bottom",
          },
        });

        tlFinal
          .to(".hbaCircleIndex", { opacity: 0, duration: 0.05 }, 0)
          .to(
            ".hbaBorder1, .hbaBg1",
            { y: 0, width: "40vw", height: "40vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorder2, .hbaBg2",
            { y: 0, width: "24vw", height: "24vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorder3, .hbaBg3",
            { y: 0, width: "16vw", height: "16vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorder4, .hbaBg4",
            { y: 0, width: "8vw", height: "8vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorder5, .hbaBg5",
            {
              y: 0,
              width: "32vw",
              height: "32vw",
              duration: shrinkSpeed,
              onComplete: animateOut,
            },
            0
          );
      },

      // MOBILE
      "(max-width: 1023px)": function () {
        // toggle nav header background color and border
        ScrollTrigger.create({
          trigger: ".slides",
          start: "top top",
          onUpdate: ({ progress }) => {
            if (progress > 0.005) {
              gsap.set("header", {
                background: "#f2eee9",
                borderBottom: "1px solid black",
              });
            } else {
              gsap.set("header", {
                background: "inherit",
                borderBottom: "",
              });
            }
          },
        });

        // change background of initial slides
        slides.forEach((slide, i) => {
          ScrollTrigger.create({
            trigger: slide,
            start: "center center",
            onEnter: () => {
              if (i > 0) {
                let slideColor = slide.dataset.scrollcolor;
                gsap.to(".slides", { backgroundColor: slideColor, duration: 0.3 });
              }
            },
            onEnterBack: () => {
              let slideColor = slide.dataset.scrollcolor;
              gsap.to(".slides", { backgroundColor: slideColor, duration: 0.3 });
            },
          });
        });

        // toggle position of circle content
        // essentially creating a manual sticky element
        ScrollTrigger.create({
          trigger: ".hbaHeadline",
          start: "bottom top",
          onEnter: () => {
            gsap.set(".circlesMobile", { position: "fixed", overflow: "visible" });
          },
          onLeaveBack: () => {
            gsap.set(".circlesMobile", {
              position: "absolute",
              overflow: "hidden",
            });
          },
        });

        // toggle opacity of corresponding circle
        frames.forEach((frame, i) => {
          ScrollTrigger.create({
            trigger: frame,
            start: "center center+=100",
            onEnter: () => {
              gsap.to(".hbaBlock" + i, { opacity: 1, duration: 0.25 });
              gsap.to(".hbaBgm" + (i + 1), { opacity: 1, duration: 0.5 });
              gsap.to(".hbaBorderm" + (i + 1), { opacity: 1, duration: 0.5 });
              if (i != blocks.length) {
                gsap.to(".hbaBlock" + (i - 1), { opacity: 0, duration: 0.25 });
              }
            },
            onLeaveBack: () => {
              if (i != 0 && i != blocks.length) {
                gsap.to(".hbaBgm" + (i + 1), { opacity: 0, duration: 0.5 });
                gsap.to(".hbaBorderm" + (i + 1), { opacity: 0, duration: 0.5 });
                gsap.to(".hbaBlock" + i, { opacity: 0, duration: 0.25 });
                gsap.to(".hbaBlock" + (i - 1), { opacity: 1, duration: 0.25 });
              }
            },
          });
        });

        // final animation of collapsing circles
        let tlFinal = gsap.timeline({
          scrollTrigger: {
            trigger: ".hbacf6",
            scrub: true,
            start: "top bottom",
            end: "center center",
          },
        });

        tlFinal
          .to(
            ".hbaBorderm1, .hbaBgm1",
            { y: 0, width: "70vw", height: "70vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorderm2, .hbaBgm2",
            { y: 0, width: "40vw", height: "40vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorderm3, .hbaBgm3",
            { y: 0, width: "25vw", height: "25vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorderm4, .hbaBgm4",
            { y: 0, width: "10vw", height: "10vw", duration: shrinkSpeed },
            0
          )
          .to(
            ".hbaBorderm5, .hbaBgm5",
            {
              y: 0,
              width: "55vw",
              height: "55vw",
              duration: shrinkSpeed,
              onComplete: animateOutMobile,
            },
            0
          );
      },
    });

    // reload page content on scrolltrigger refresh to configure
    // correct animation sizing
    return () => {
      // kill all triggers
      let triggers = ScrollTrigger.getAll();
      triggers.forEach((trigger) => {
        trigger.kill();
      });
      bod.classList.remove("hasAnimated");
    };
  }, [windowHeight]);
  return (
    <div style={{ "--vh": `${height}px` }} className={`${styles.root}`} ref={ref}>
      <div className={`philContent`}>
        <ul className={`${styles.introslides} slides`}>
          {props.slides &&
            props.slides.map((node, index) => {
              return (
                <li
                  key={index}
                  className={`${styles.introslide} slide slide${index}`}
                  data-scrollcolor={node.slideColor.hex}
                >
                  <div className={`${styles.introslideinner} ${theme.gdcont} ${theme.gmcont}`}>
                    <div className={`${styles.copy} ${theme.gds9} ${theme.gde12} ${theme.gmmain}`}>
                      {index == 0 && (
                        <>
                          <p className={`${styles.extra}`}>{node.slideCopy2}</p>
                        </>
                      )}
                    </div>
                    <div className={`${styles.slidelines} ${theme.gmcont} ${theme.gmfull}`}>
                      {node.slideLines &&
                        node.slideLines.map((node, index) => {
                          const gds = `gds${node.grid.gridStart}`;
                          const gde = `gde${node.grid.gridEnd}`;
                          const gms = `gms${node.gridMobile.gridStart}`;
                          const gme = `gme${node.gridMobile.gridEnd}`;
                          return (
                            <p
                              key={index}
                              className={[
                                styles.slideline,
                                theme[gds],
                                theme[gde],
                                theme[gms],
                                theme[gme],
                              ].join(" ")}
                            >
                              {node.lineText}
                            </p>
                          );
                        })}
                    </div>
                    <div className={`${styles.copy} ${theme.gds9} ${theme.gde12} ${theme.gmmain}`}>
                      <p className={``}>{node.slideCopy1}</p>
                    </div>
                  </div>
                </li>
              );
            })}
        </ul>
        <div className={`${styles.hbaHeadline} ${theme.gdcont} hbaHeadline`}>
          <h2 className={`${theme.gds1} ${theme.gde12}`}>Holistic Brand Alignment</h2>
        </div>
        <div className={`${styles.hbaSlides} blocks`}>
          <ul class={`${styles.hbaSlidesInner} blocksInner`}>
            {props.hbaSlides &&
              props.hbaSlides.map((node, index) => {
                if (index < 5) {
                  return (
                    <li key={index} className={`${styles.hbaSlide} block hbaBlock${index}`}>
                      <div className={`${styles.hbaSlideInner} ${theme.gdcont}`}>
                        <div
                          className={`${styles.copy} ${theme.gds1} ${theme.gde6} ${styles.hbaTextBlock}`}
                        >
                          <h3 className={`${styles.hbaTitle}`}>
                            {node.philosophyHbaSlideHeadline}
                          </h3>
                          <div>
                            <div className={`${styles.hbaBodyIndex}`}>{index + 1}</div>
                            <div className={`${styles.hbaBody}`}>
                              <div>
                                <h4>{node.philosophyHbaSlidetitle}</h4>
                              </div>
                              <p className={``}>{node.philosophyHbaSlideCopy}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                  );
                }
              })}
            <li className={`${styles.hbaSlideFinal} hbaSlideFinal`}></li>
          </ul>
          <div className={`${styles.hbaCircles} circles`}>
            <div className={`${styles.bgCircle} ${styles.bgCircle18} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle17} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle16} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle15} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle14} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle13} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle12} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle11} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle10} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle9} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle8} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle7} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle6} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle5} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle4} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle3} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle2} bgCircle`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle1} bgCircle`}></div>
            <div className={`${styles.hbaCircleBg1} hbaBg1`}></div>
            <div className={`${styles.hbaCircleBg5} hbaBg5`}></div>
            <div className={`${styles.hbaCircleBg2} hbaBg2`}></div>
            <div className={`${styles.hbaCircleBg3} hbaBg3`}></div>
            <div className={`${styles.hbaCircleBg4} hbaBg4`}></div>
            <div className={`${styles.hbaCircle1} hbaBorder1`}>
              <p className={`hbaCircleIndex`}>1</p>
            </div>
            <div className={`${styles.hbaCircle5} hbaBorder5`}>
              <p className={`hbaCircleIndex`}>5</p>
            </div>
            <div className={`${styles.hbaCircle2} hbaBorder2`}>
              <p className={`hbaCircleIndex`}>2</p>
            </div>
            <div className={`${styles.hbaCircle3} hbaBorder3`}>
              <p className={`hbaCircleIndex`}>3</p>
            </div>
            <div className={`${styles.hbaCircle4} hbaBorder4`}>
              <p className={`hbaCircleIndex`}>4</p>
            </div>
          </div>

          <div className={`${styles.hbaCirclesMobile} circlesMobile`}>
            <div className={`${styles.hbaCircleSlides}`}>
              <div className={`${styles.hbaCircleSlideShapes} ${styles.hbacsShapes} hbacsShapes`}>
                <div className={`${styles.hbaCircleBg1} hbaBgm1`}></div>
                <div className={`${styles.hbaCircleBg5} hbaBgm5`}></div>
                <div className={`${styles.hbaCircleBg2} hbaBgm2`}></div>
                <div className={`${styles.hbaCircleBg3} hbaBgm3`}></div>
                <div className={`${styles.hbaCircleBg4} hbaBgm4`}></div>
                <div className={`${styles.hbaCircle1} hbaBorderm1`}></div>
                <div className={`${styles.hbaCircle5} hbaBorderm5`}></div>
                <div className={`${styles.hbaCircle2} hbaBorderm2`}></div>
                <div className={`${styles.hbaCircle3} hbaBorderm3`}></div>
                <div className={`${styles.hbaCircle4} hbaBorderm4`}></div>
              </div>
            </div>
          </div>

          <div className={`${styles.hbaCirclesMobile} ${styles.hbacmFull} circlesFull`}>
            <div className={`${styles.bgCircle} ${styles.bgCircle13} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle12} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle11} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle10} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle9} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle8} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle7} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle6} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle5} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle4} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle3} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle2} bgCircleMobile`}></div>
            <div className={`${styles.bgCircle} ${styles.bgCircle1} bgCircleMobile`}></div>
            <div className={`${styles.hbaCircleBg1} hbaBgm1`}></div>
            <div className={`${styles.hbaCircleBg5} hbaBgm5`}></div>
            <div className={`${styles.hbaCircleBg2} hbaBgm2`}></div>
            <div className={`${styles.hbaCircleBg3} hbaBgm3`}></div>
            <div className={`${styles.hbaCircleBg4} hbaBgm4`}></div>
            <div className={`${styles.hbaCircle1} hbaBorderm1`}></div>
            <div className={`${styles.hbaCircle5} hbaBorderm5`}></div>
            <div className={`${styles.hbaCircle2} hbaBorderm2`}></div>
            <div className={`${styles.hbaCircle3} hbaBorderm3`}></div>
            <div className={`${styles.hbaCircle4} hbaBorderm4`}></div>
          </div>

          <div className={`${styles.hbaCircleFrames} circleFrames`}>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf1} frame hbacf1`}></div>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf2} frame hbacf2`}></div>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf3} frame hbacf3`}></div>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf4} frame hbacf4`}></div>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf5} frame hbacf5`}></div>
            <div className={`${styles.hbaCircleFrame} ${styles.hbacf6} frame hbacf6`}></div>
          </div>
        </div>
      </div>

      <CoCreateContact
        title={props.footerHeadline}
        columns={props.footerColumns}
        rootClass={`${styles.finalFrameWrapper} finalFrameWrapper`}
        wrapperClass={`${styles.finalFrame} finalFrame`}
        titleClass={`${styles.finalFrameTitle} finalFrameTitle`}
        columnClass={`${styles.finalFrameColumn} finalFrameColumn`}
      />

      <ProjectsGrid nodes={props.projectGridNodes} controls={true} />
    </div>
  );
}

export default PhilosophyContent;
